/**
 * BJCP 2021 beer style guidelines — categories 1–12.
 *
 * Covers the lager-and-bitter half of the guidelines: standard American and
 * international lagers, the Czech and German lager families, German wheat
 * beers, British bitter and the pale Commonwealth ales.
 *
 * The `vitals` are facts — published numeric ranges — and are reproduced as
 * published. The `overallImpression` prose is **ours**, written from scratch
 * (PLAN §6, licensing note); it is not BJCP text and is not a paraphrase of
 * it. Where a range is reconstructed rather than verified against the
 * published table the entry is marked `representative`.
 */

import type { Style } from '../../schema/style';

/** BJCP 2021 sub-styles, categories 1–12. @see Style */
export const styleCategories01To12: readonly Style[] = [
  // -------------------------------------------------------------------------
  // 1. Standard American Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-1a',
    guideline: 'bjcp-2021',
    categoryNumber: 1,
    categoryLetter: 'A',
    name: 'American Light Lager',
    categoryName: 'Standard American Beer',
    vitals: {
      og: { min: 1.028, max: 1.04 },
      fg: { min: 0.998, max: 1.008 },
      ibu: { min: 8, max: 12 },
      srm: { min: 2, max: 3 },
      abv: { min: 2.8, max: 4.2 },
    },
    overallImpression:
      'A very pale, very light-bodied lager built for volume drinking, with barely any malt or hop flavor to speak of. Rice or maize thins the body deliberately and the finish is fast and dry. Because there is nothing to hide behind, judges reward flawless fermentation and sheer drinkability rather than character.',
    commercialExamples: ['Bud Light', 'Coors Light', 'Miller Lite', 'Michelob Ultra'],
    tags: ['lager', 'pale', 'american', 'adjunct', 'sessionable', 'light'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-1b',
    guideline: 'bjcp-2021',
    categoryNumber: 1,
    categoryLetter: 'B',
    name: 'American Lager',
    categoryName: 'Standard American Beer',
    vitals: {
      og: { min: 1.04, max: 1.05 },
      fg: { min: 1.004, max: 1.01 },
      ibu: { min: 8, max: 18 },
      srm: { min: 2, max: 4 },
      abv: { min: 4.2, max: 5.3 },
    },
    overallImpression:
      'The mainstream American lager: a little more malt and body than the light version, still built on maize or rice to keep it pale and crisp. High carbonation and low bitterness push it firmly towards refreshment. Anything roasty, fruity or sulfury reads as a fault.',
    commercialExamples: [
      'Budweiser',
      'Coors Original',
      'Miller High Life',
      'Pabst Blue Ribbon',
      'Full Sail Session Premium Lager',
    ],
    tags: ['lager', 'pale', 'american', 'adjunct', 'sessionable', 'crisp'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-1c',
    guideline: 'bjcp-2021',
    categoryNumber: 1,
    categoryLetter: 'C',
    name: 'Cream Ale',
    categoryName: 'Standard American Beer',
    vitals: {
      og: { min: 1.042, max: 1.055 },
      fg: { min: 1.006, max: 1.012 },
      ibu: { min: 8, max: 20 },
      srm: { min: 2.5, max: 5 },
      abv: { min: 4.2, max: 5.6 },
    },
    overallImpression:
      'An ale fermented cool and then lagered so that it drinks like an American lager, with a touch more malt sweetness and only a hint of yeast fruit. Maize or sugar keeps it pale and highly attenuated. The target is a smooth, easy pale beer with no rough edges anywhere.',
    commercialExamples: [
      'Genesee Cream Ale',
      'Little Kings Cream Ale',
      'New Glarus Spotted Cow',
      'Sixpoint Sweet Action',
    ],
    tags: ['ale', 'pale', 'american', 'adjunct', 'sessionable', 'crisp'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-1d',
    guideline: 'bjcp-2021',
    categoryNumber: 1,
    categoryLetter: 'D',
    name: 'American Wheat Beer',
    categoryName: 'Standard American Beer',
    vitals: {
      og: { min: 1.04, max: 1.055 },
      fg: { min: 1.008, max: 1.013 },
      ibu: { min: 15, max: 30 },
      srm: { min: 3, max: 6 },
      abv: { min: 4.0, max: 5.5 },
    },
    overallImpression:
      'A pale refreshing wheat beer fermented with a clean American or British yeast, so the banana and clove of Bavaria are absent. Wheat supplies soft bready grain and often a hazy pour, while hopping runs from restrained to openly citrusy. Fermentation cleanliness is what separates it from a Weissbier.',
    commercialExamples: [
      'Bell Oberon',
      'Boulevard Unfiltered Wheat Beer',
      'Widmer Brothers Hefeweizen',
      'Three Floyds Gumballhead',
    ],
    tags: ['ale', 'wheat', 'pale', 'american', 'sessionable'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 2. International Lager
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-2a',
    guideline: 'bjcp-2021',
    categoryNumber: 2,
    categoryLetter: 'A',
    name: 'International Pale Lager',
    categoryName: 'International Lager',
    vitals: {
      og: { min: 1.042, max: 1.05 },
      fg: { min: 1.008, max: 1.012 },
      ibu: { min: 18, max: 25 },
      srm: { min: 2, max: 6 },
      abv: { min: 4.6, max: 6.0 },
    },
    overallImpression:
      'The global lager template, brewed from Mexico to Japan: pale, clean and lightly bitter, with a shade more malt and hop presence than its American cousin. Adjuncts are still common and the finish stays crisp. Judges are looking for freshness and clean attenuation rather than complexity.',
    commercialExamples: [
      'Heineken',
      'Stella Artois',
      'Corona Extra',
      'Asahi Super Dry',
      'Birra Moretti',
    ],
    tags: ['lager', 'pale', 'international', 'crisp', 'sessionable'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-2b',
    guideline: 'bjcp-2021',
    categoryNumber: 2,
    categoryLetter: 'B',
    name: 'International Amber Lager',
    categoryName: 'International Lager',
    vitals: {
      og: { min: 1.042, max: 1.055 },
      fg: { min: 1.008, max: 1.014 },
      ibu: { min: 8, max: 25 },
      srm: { min: 7, max: 14 },
      abv: { min: 4.6, max: 6.0 },
    },
    overallImpression:
      'An amber mass-market lager: modest caramel or toast laid over a body that stays light and a fermentation that stays lager-clean. Hop character is low and sweetness is restrained. Treat it as a color and light malt accent on the pale international lager, not as a Vienna or a Märzen.',
    commercialExamples: [
      'Dos Equis Ambar',
      'Yuengling Traditional Lager',
      'Brooklyn Lager',
      'Capital Wisconsin Amber',
    ],
    tags: ['lager', 'amber', 'international', 'malty', 'sessionable'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-2c',
    guideline: 'bjcp-2021',
    categoryNumber: 2,
    categoryLetter: 'C',
    name: 'International Dark Lager',
    categoryName: 'International Lager',
    vitals: {
      og: { min: 1.044, max: 1.056 },
      fg: { min: 1.008, max: 1.012 },
      ibu: { min: 8, max: 20 },
      srm: { min: 14, max: 22 },
      abv: { min: 4.2, max: 6.0 },
    },
    overallImpression:
      'A dark version of the international lager: color plus a suggestion of toast or caramel, without real roast bite or weight. Bitterness is low and sweetness is kept in check so it still drinks easily and cold. Dark color on a light, clean palate is the signature.',
    commercialExamples: ['Negra Modelo', 'Baltika #4', 'San Miguel Dark', 'Shiner Bock'],
    tags: ['lager', 'dark', 'international', 'sessionable'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 3. Czech Lager
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-3a',
    guideline: 'bjcp-2021',
    categoryNumber: 3,
    categoryLetter: 'A',
    name: 'Czech Pale Lager',
    categoryName: 'Czech Lager',
    vitals: {
      og: { min: 1.028, max: 1.044 },
      fg: { min: 1.008, max: 1.014 },
      ibu: { min: 20, max: 35 },
      srm: { min: 3, max: 6 },
      abv: { min: 3.0, max: 4.1 },
    },
    overallImpression:
      'The everyday Czech tap beer: low gravity, soft water, bready Bohemian malt and a generous helping of Saaz. It tastes considerably fuller and more bitter than the gravity suggests, thanks to traditional mashing and long lagering. The brewer is chasing flavor density at session strength.',
    commercialExamples: ['Notch Session Pils', 'Březnický Ležák', 'Únětické Pivo 10°'],
    tags: ['lager', 'czech', 'pale', 'sessionable', 'hoppy', 'saaz'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-3b',
    guideline: 'bjcp-2021',
    categoryNumber: 3,
    categoryLetter: 'B',
    name: 'Czech Premium Pale Lager',
    categoryName: 'Czech Lager',
    vitals: {
      og: { min: 1.044, max: 1.06 },
      fg: { min: 1.013, max: 1.017 },
      ibu: { min: 30, max: 45 },
      srm: { min: 3.5, max: 6 },
      abv: { min: 4.2, max: 5.8 },
    },
    overallImpression:
      'The original golden pilsner: rich bready Bohemian malt set against a rounded, firm Saaz bitterness and a genuinely floral-spicy hop aroma. Soft water and traditional mashing leave more body and less attenuation than a German pils. Even at forty IBU the balance should still read as malty.',
    commercialExamples: [
      'Pilsner Urquell',
      'Budweiser Budvar',
      'Kout na Šumavě Koutská 12°',
      'Gambrinus Premium',
      'Bernard Sváteční Ležák',
    ],
    tags: ['lager', 'czech', 'pale', 'hoppy', 'malty', 'saaz'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-3c',
    guideline: 'bjcp-2021',
    categoryNumber: 3,
    categoryLetter: 'C',
    name: 'Czech Amber Lager',
    categoryName: 'Czech Lager',
    vitals: {
      og: { min: 1.044, max: 1.06 },
      fg: { min: 1.013, max: 1.017 },
      ibu: { min: 20, max: 35 },
      srm: { min: 10, max: 16 },
      abv: { min: 4.4, max: 5.8 },
    },
    overallImpression:
      'A Czech lager built on caramel and Munich malts, giving toasted depth and sometimes a light caramel sweetness with the Saaz signature still audible underneath. Bitterness is moderate and supportive rather than assertive. It should read as the maltier sibling of the pale Czech lager, never as a sweet or roasty beer.',
    commercialExamples: [
      'Bernard Jantarový Ležák',
      'Primátor Polotmavý 13°',
      'Vysoký Chlumec Démon',
    ],
    tags: ['lager', 'czech', 'amber', 'malty', 'saaz'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-3d',
    guideline: 'bjcp-2021',
    categoryNumber: 3,
    categoryLetter: 'D',
    name: 'Czech Dark Lager',
    categoryName: 'Czech Lager',
    vitals: {
      og: { min: 1.044, max: 1.06 },
      fg: { min: 1.013, max: 1.017 },
      ibu: { min: 18, max: 34 },
      srm: { min: 14, max: 35 },
      abv: { min: 4.4, max: 5.8 },
    },
    overallImpression:
      'Dark Czech lager with cocoa, toast and caramel from dark malts, held smooth by soft water and a long cold conditioning. Saaz sits behind the malt but keeps the finish from turning cloying. Roast should stay gentle — this is neither a Schwarzbier nor a stout.',
    commercialExamples: [
      'U Fleků Flekovský Tmavý Ležák',
      'Budweiser Budvar Tmavý Ležák',
      'Kout na Šumavě Tmavé 14°',
      'Bohemia Regent Tmavý Ležák',
    ],
    tags: ['lager', 'czech', 'dark', 'malty', 'saaz'],
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // 4. Pale Malty European Lager
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-4a',
    guideline: 'bjcp-2021',
    categoryNumber: 4,
    categoryLetter: 'A',
    name: 'Munich Helles',
    categoryName: 'Pale Malty European Lager',
    vitals: {
      og: { min: 1.044, max: 1.048 },
      fg: { min: 1.006, max: 1.012 },
      ibu: { min: 16, max: 22 },
      srm: { min: 3, max: 5 },
      abv: { min: 4.7, max: 5.4 },
    },
    overallImpression:
      'Munich in a glass: soft bready pilsner malt in the foreground with just enough noble hop to dry the finish. No caramel, no roast, no fruit — malt-forward yet still quick and clean to drink. Every flaw is naked here, so technique matters more than recipe.',
    commercialExamples: [
      'Weihenstephaner Original',
      'Augustiner Lagerbier Hell',
      'Paulaner Original Münchner Hell',
      'Spaten Premium Lager',
    ],
    tags: ['lager', 'german', 'pale', 'malty', 'sessionable', 'helles'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-4b',
    guideline: 'bjcp-2021',
    categoryNumber: 4,
    categoryLetter: 'B',
    name: 'Festbier',
    categoryName: 'Pale Malty European Lager',
    vitals: {
      og: { min: 1.054, max: 1.057 },
      fg: { min: 1.01, max: 1.012 },
      ibu: { min: 18, max: 25 },
      srm: { min: 4, max: 7 },
      abv: { min: 5.8, max: 6.3 },
    },
    overallImpression:
      'The beer actually poured at the modern Oktoberfest: gold rather than amber, stronger than a Helles but still made for the liter mug. Rich pilsner and Vienna malt carry it, with hops present mainly to stop it turning sweet. The trick is keeping six percent smooth enough to drink all afternoon.',
    commercialExamples: [
      'Paulaner Oktoberfest Bier',
      'Augustiner Oktoberfest',
      'Hacker-Pschorr Superior Festbier',
      'Weihenstephaner Festbier',
    ],
    tags: ['lager', 'german', 'pale', 'malty', 'oktoberfest'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-4c',
    guideline: 'bjcp-2021',
    categoryNumber: 4,
    categoryLetter: 'C',
    name: 'Helles Bock',
    categoryName: 'Pale Malty European Lager',
    vitals: {
      og: { min: 1.064, max: 1.072 },
      fg: { min: 1.011, max: 1.018 },
      ibu: { min: 23, max: 35 },
      srm: { min: 6, max: 11 },
      abv: { min: 6.3, max: 7.4 },
    },
    overallImpression:
      'A pale bock — all the malt intensity of the style without the darker toast and caramel. Pilsner and Munich malts give a rich, lightly toasty sweetness that the alcohol warms rather than overwhelms. Hopping is a touch firmer than a dark bock so the finish lands drier and cleaner.',
    commercialExamples: [
      'Ayinger Maibock',
      'Einbecker Mai-Ur-Bock',
      'Hofbräu Maibock',
      'Capital Maibock',
    ],
    tags: ['lager', 'german', 'pale', 'malty', 'strong', 'bock'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 5. Pale Bitter European Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-5a',
    guideline: 'bjcp-2021',
    categoryNumber: 5,
    categoryLetter: 'A',
    name: 'German Leichtbier',
    categoryName: 'Pale Bitter European Beer',
    vitals: {
      og: { min: 1.026, max: 1.034 },
      fg: { min: 1.006, max: 1.01 },
      ibu: { min: 15, max: 28 },
      srm: { min: 2, max: 5 },
      abv: { min: 2.4, max: 3.6 },
    },
    overallImpression:
      'A German pils scaled down in gravity but not in hop character, so it stays crisp and properly bitter instead of watery. Pale malt, noble hops and an utterly clean fermentation are all there is to it. Success means a low-alcohol beer that still tastes like a real lager.',
    commercialExamples: ['Bitburger Light', 'Mahrs Leicht', 'Paulaner Münchner Leicht'],
    tags: ['lager', 'german', 'pale', 'sessionable', 'hoppy', 'light'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-5b',
    guideline: 'bjcp-2021',
    categoryNumber: 5,
    categoryLetter: 'B',
    name: 'Kölsch',
    categoryName: 'Pale Bitter European Beer',
    vitals: {
      og: { min: 1.044, max: 1.05 },
      fg: { min: 1.007, max: 1.011 },
      ibu: { min: 18, max: 30 },
      srm: { min: 3.5, max: 5 },
      abv: { min: 4.4, max: 5.2 },
    },
    overallImpression:
      "Cologne's pale ale, fermented warm with ale yeast and then lagered cold, which leaves a whisper of fruit under a crisp, gently bitter body. Everything about it is understated; balance and subtlety count for more than any single flavor. It should feel as clean as a lager but a shade softer.",
    commercialExamples: ['Reissdorf Kölsch', 'Gaffel Kölsch', 'Früh Kölsch', 'Sünner Kölsch'],
    tags: ['ale', 'german', 'pale', 'kolsch', 'sessionable', 'crisp'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-5c',
    guideline: 'bjcp-2021',
    categoryNumber: 5,
    categoryLetter: 'C',
    name: 'German Helles Exportbier',
    categoryName: 'Pale Bitter European Beer',
    vitals: {
      og: { min: 1.048, max: 1.056 },
      fg: { min: 1.01, max: 1.015 },
      ibu: { min: 20, max: 30 },
      srm: { min: 4, max: 7 },
      abv: { min: 4.8, max: 6.0 },
    },
    overallImpression:
      'The Dortmund style, sitting squarely between its neighbors: stronger and fuller than a pils, drier and more bitter than a Helles. Sulphate-rich water gives a firm mineral bitterness that keeps the malt honest. Balance is the entire point of the style.',
    commercialExamples: [
      'DAB Original',
      'Dortmunder Kronen',
      'Great Lakes Dortmunder Gold',
      'Gordon Biersch Golden Export',
    ],
    tags: ['lager', 'german', 'pale', 'malty', 'hoppy', 'export'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-5d',
    guideline: 'bjcp-2021',
    categoryNumber: 5,
    categoryLetter: 'D',
    name: 'German Pils',
    categoryName: 'Pale Bitter European Beer',
    vitals: {
      og: { min: 1.044, max: 1.05 },
      fg: { min: 1.008, max: 1.013 },
      ibu: { min: 22, max: 40 },
      srm: { min: 2, max: 5 },
      abv: { min: 4.4, max: 5.2 },
    },
    overallImpression:
      "Germany's benchmark pale lager: light cracker malt driven by a firm, clean noble hop bitterness and a floral-spicy aroma. Sulphate water makes the finish crisp and the bitterness linger. The target is bright, dry, bitter and absolutely spotless.",
    commercialExamples: [
      'Bitburger Premium Pils',
      'König Pilsener',
      'Jever Pilsener',
      'Trumer Pils',
      'Left Hand Polestar Pilsner',
    ],
    tags: ['lager', 'german', 'pale', 'hoppy', 'crisp', 'pilsner'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 6. Amber Malty European Lager
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-6a',
    guideline: 'bjcp-2021',
    categoryNumber: 6,
    categoryLetter: 'A',
    name: 'Märzen',
    categoryName: 'Amber Malty European Lager',
    vitals: {
      og: { min: 1.054, max: 1.06 },
      fg: { min: 1.01, max: 1.014 },
      ibu: { min: 18, max: 24 },
      srm: { min: 8, max: 17 },
      abv: { min: 5.6, max: 6.3 },
    },
    overallImpression:
      'The malt-forward amber lager that used to be the Oktoberfest beer: Munich malt bread crust and toast, no caramel sweetness, and a dry lager finish. Hops appear only as balance. The aim is a big malt aroma over a beer that still finishes clean enough to drink by the liter.',
    commercialExamples: [
      'Ayinger Oktober Fest-Märzen',
      'Paulaner Oktoberfest Märzen',
      'Hacker-Pschorr Original Oktoberfest',
      'Great Lakes Oktoberfest',
    ],
    tags: ['lager', 'german', 'amber', 'malty', 'oktoberfest'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-6b',
    guideline: 'bjcp-2021',
    categoryNumber: 6,
    categoryLetter: 'B',
    name: 'Rauchbier',
    categoryName: 'Amber Malty European Lager',
    vitals: {
      og: { min: 1.05, max: 1.057 },
      fg: { min: 1.012, max: 1.016 },
      ibu: { min: 18, max: 30 },
      srm: { min: 12, max: 22 },
      abv: { min: 4.8, max: 6.0 },
    },
    overallImpression:
      'A Märzen brewed with beechwood-smoked malt, so woodsmoke sits over toasty amber malt. The smoke has to marry into the malt rather than dominate, and should never read as ash, soot or bacon. Underneath the smoke it still has to be a clean, well-lagered German amber beer.',
    commercialExamples: [
      'Schlenkerla Rauchbier Märzen',
      'Spezial Rauchbier Märzen',
      'Kaiserdom Rauchbier',
      'Eisenbahn Rauchbier',
    ],
    tags: ['lager', 'german', 'amber', 'malty', 'smoked'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-6c',
    guideline: 'bjcp-2021',
    categoryNumber: 6,
    categoryLetter: 'C',
    name: 'Dunkles Bock',
    categoryName: 'Amber Malty European Lager',
    vitals: {
      og: { min: 1.064, max: 1.072 },
      fg: { min: 1.013, max: 1.019 },
      ibu: { min: 20, max: 27 },
      srm: { min: 14, max: 22 },
      abv: { min: 6.3, max: 7.2 },
    },
    overallImpression:
      'The traditional dark bock: deep amber to brown, rich in Munich malt toast and light caramel, warmed by alcohol and bittered only enough to stop it cloying. Roast and hop flavor both stay out of the picture. Months of cold lagering are what make the strength drinkable.',
    commercialExamples: ['Einbecker Ur-Bock Dunkel', 'Aass Bock', 'New Glarus Uff-da Bock'],
    tags: ['lager', 'german', 'dark', 'malty', 'strong', 'bock'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 7. Amber Bitter European Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-7a',
    guideline: 'bjcp-2021',
    categoryNumber: 7,
    categoryLetter: 'A',
    name: 'Vienna Lager',
    categoryName: 'Amber Bitter European Beer',
    vitals: {
      og: { min: 1.048, max: 1.055 },
      fg: { min: 1.01, max: 1.014 },
      ibu: { min: 18, max: 30 },
      srm: { min: 9, max: 15 },
      abv: { min: 4.7, max: 5.5 },
    },
    overallImpression:
      'An elegant amber lager driven by the toasty character of Vienna malt, finishing dry with moderate hopping and no caramel sweetness. It is lighter and less malty than a Märzen, trading bread crust for toast. Clean lager fermentation underneath is non-negotiable.',
    commercialExamples: [
      'Devils Backbone Vienna Lager',
      'Great Lakes Eliot Ness',
      'Schell Firebrick',
      'Figueroa Mountain Danish Red Lager',
    ],
    tags: ['lager', 'amber', 'malty', 'austrian', 'sessionable'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-7b',
    guideline: 'bjcp-2021',
    categoryNumber: 7,
    categoryLetter: 'B',
    name: 'Altbier',
    categoryName: 'Amber Bitter European Beer',
    vitals: {
      og: { min: 1.044, max: 1.052 },
      fg: { min: 1.008, max: 1.014 },
      ibu: { min: 25, max: 50 },
      srm: { min: 11, max: 17 },
      abv: { min: 4.3, max: 5.5 },
    },
    overallImpression:
      "Düsseldorf's copper ale, fermented cool with ale yeast and then lagered, so it drinks smooth and clean while carrying a genuinely assertive bitterness. Nutty toasted malt forms the base but the finish is firmly dry. The balance leans bitter while the character leans lager.",
    commercialExamples: [
      'Uerige Altbier',
      'Füchschen Alt',
      'Schumacher Alt',
      'Diebels Alt',
      'Long Trail Ale',
    ],
    tags: ['ale', 'german', 'amber', 'hoppy', 'malty', 'altbier'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-7c',
    guideline: 'bjcp-2021',
    categoryNumber: 7,
    categoryLetter: 'C',
    name: 'Kellerbier',
    categoryName: 'Amber Bitter European Beer',
    vitals: {
      og: { min: 1.045, max: 1.06 },
      fg: { min: 1.008, max: 1.015 },
      ibu: { min: 20, max: 35 },
      srm: { min: 3, max: 17 },
      abv: { min: 4.7, max: 5.8 },
    },
    overallImpression:
      'Unfiltered German lager served young and often straight from the cask, which makes it rounder, softer and hazier than the filtered version of the same beer. Depending on the brewery it can be pale or amber. Freshness, low carbonation and a lively hop or yeast lift are the whole appeal.',
    commercialExamples: [
      'Mahrs Bräu Ungespundet',
      'St. Georgen Bräu Keller Bier',
      'Löwenbräu Buttenheim Ungespundetes',
      'Tucher Kellerbier',
    ],
    tags: ['lager', 'german', 'unfiltered', 'malty', 'hoppy', 'kellerbier'],
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // 8. Dark European Lager
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-8a',
    guideline: 'bjcp-2021',
    categoryNumber: 8,
    categoryLetter: 'A',
    name: 'Munich Dunkel',
    categoryName: 'Dark European Lager',
    vitals: {
      og: { min: 1.048, max: 1.056 },
      fg: { min: 1.01, max: 1.016 },
      ibu: { min: 18, max: 28 },
      srm: { min: 14, max: 28 },
      abv: { min: 4.5, max: 5.6 },
    },
    overallImpression:
      "Munich's dark everyday lager: deep brown, full of bread crust, toast and a hint of chocolate from Munich malt, finishing smooth and clean. Both roast bitterness and caramel sweetness are out of place. The target is rich malt complexity that never turns heavy.",
    commercialExamples: [
      'Ayinger Altbairisch Dunkel',
      'Weltenburger Kloster Barock Dunkel',
      'Hacker-Pschorr Alt Munich Dark',
      'Paulaner Alt Münchner Dunkel',
    ],
    tags: ['lager', 'german', 'dark', 'malty', 'sessionable'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-8b',
    guideline: 'bjcp-2021',
    categoryNumber: 8,
    categoryLetter: 'B',
    name: 'Schwarzbier',
    categoryName: 'Dark European Lager',
    vitals: {
      og: { min: 1.046, max: 1.052 },
      fg: { min: 1.01, max: 1.016 },
      ibu: { min: 20, max: 30 },
      srm: { min: 17, max: 30 },
      abv: { min: 4.4, max: 5.4 },
    },
    overallImpression:
      'A near-black German lager that drinks far lighter than it looks: gentle roast, a touch of cocoa, and a dry crisp finish with noticeable noble hop bitterness. Debittered dark malts supply the color without the harshness. Think pils manners in dark clothing, not a lagered stout.',
    commercialExamples: [
      'Köstritzer Schwarzbier',
      'Einbecker Schwarzbier',
      'Kulmbacher Mönchshof Premium Schwarzbier',
      'Devils Backbone Schwartz Bier',
    ],
    tags: ['lager', 'german', 'dark', 'roasty', 'sessionable'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 9. Strong European Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-9a',
    guideline: 'bjcp-2021',
    categoryNumber: 9,
    categoryLetter: 'A',
    name: 'Doppelbock',
    categoryName: 'Strong European Beer',
    vitals: {
      og: { min: 1.072, max: 1.112 },
      fg: { min: 1.016, max: 1.024 },
      ibu: { min: 16, max: 26 },
      srm: { min: 6, max: 25 },
      abv: { min: 7.0, max: 10.0 },
    },
    overallImpression:
      'The strong Bavarian malt beer: deep melanoidin richness, bread crust and dark fruit, warmed by alcohol and barely bittered at all. Pale and dark versions both exist and both are unmistakably malt-driven. The best examples hide their strength behind sheer smoothness.',
    commercialExamples: [
      'Ayinger Celebrator',
      'Paulaner Salvator',
      'Weihenstephaner Korbinian',
      'Andechser Doppelbock Dunkel',
      'Spaten Optimator',
    ],
    tags: ['lager', 'german', 'dark', 'malty', 'strong', 'bock'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-9b',
    guideline: 'bjcp-2021',
    categoryNumber: 9,
    categoryLetter: 'B',
    name: 'Eisbock',
    categoryName: 'Strong European Beer',
    vitals: {
      og: { min: 1.078, max: 1.12 },
      fg: { min: 1.02, max: 1.035 },
      ibu: { min: 25, max: 35 },
      srm: { min: 18, max: 30 },
      abv: { min: 9.0, max: 14.0 },
    },
    overallImpression:
      'A doppelbock concentrated by freezing the beer and removing the ice, which pushes malt, dark fruit and alcohol to an extreme. It should be intense and warming yet still smooth, never hot or solvent-like. Holding balance at that strength is the whole challenge.',
    commercialExamples: [
      'Kulmbacher Reichelbräu Eisbock',
      'Schneider Aventinus Eisbock',
      'Kulmbacher Eisbock',
    ],
    tags: ['lager', 'german', 'dark', 'malty', 'strong', 'bock'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-9c',
    guideline: 'bjcp-2021',
    categoryNumber: 9,
    categoryLetter: 'C',
    name: 'Baltic Porter',
    categoryName: 'Strong European Beer',
    vitals: {
      og: { min: 1.06, max: 1.09 },
      fg: { min: 1.016, max: 1.024 },
      ibu: { min: 20, max: 40 },
      srm: { min: 17, max: 30 },
      abv: { min: 6.5, max: 9.5 },
    },
    overallImpression:
      "A strong dark beer from the Baltic, usually cold-fermented, combining porter's roast and dark fruit with lager smoothness. The alcohol is substantial but rounded, and the roast stays restrained next to a stout. Long cold conditioning is what makes the strength disappear.",
    commercialExamples: [
      'Żywiec Porter',
      'Okocim Porter',
      'Sinebrychoff Porter',
      'Baltika #6 Porter',
      'Devils Backbone Danzig',
    ],
    tags: ['lager', 'dark', 'strong', 'roasty', 'malty', 'porter'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 10. German Wheat Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-10a',
    guideline: 'bjcp-2021',
    categoryNumber: 10,
    categoryLetter: 'A',
    name: 'Weissbier',
    categoryName: 'German Wheat Beer',
    vitals: {
      og: { min: 1.044, max: 1.052 },
      fg: { min: 1.008, max: 1.014 },
      ibu: { min: 8, max: 15 },
      srm: { min: 2, max: 6 },
      abv: { min: 4.3, max: 5.6 },
    },
    overallImpression:
      "Bavaria's pale wheat beer, defined entirely by its yeast: banana ester and clove phenol in balance over a soft bready wheat body. Hop character is almost absent and the carbonation is high and refreshing. Fermentation control, not the grist, decides whether it is any good.",
    commercialExamples: [
      'Weihenstephaner Hefeweissbier',
      'Schneider Weisse Original',
      'Paulaner Hefe-Weissbier Naturtrüb',
      'Ayinger Bräuweisse',
    ],
    tags: ['ale', 'german', 'wheat', 'pale', 'sessionable', 'weissbier'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-10b',
    guideline: 'bjcp-2021',
    categoryNumber: 10,
    categoryLetter: 'B',
    name: 'Dunkles Weissbier',
    categoryName: 'German Wheat Beer',
    vitals: {
      og: { min: 1.044, max: 1.056 },
      fg: { min: 1.01, max: 1.014 },
      ibu: { min: 10, max: 18 },
      srm: { min: 14, max: 23 },
      abv: { min: 4.3, max: 5.6 },
    },
    overallImpression:
      'The dark counterpart to Weissbier: the same banana and clove yeast character laid over Munich malt bread crust, toast and light caramel. It is richer and maltier than the pale version yet should stay every bit as lively and drinkable. Roast character is not wanted here.',
    commercialExamples: [
      'Weihenstephaner Hefeweissbier Dunkel',
      'Franziskaner Hefe-Weisse Dunkel',
      'Ayinger Ur-Weisse',
      'Erdinger Weissbier Dunkel',
    ],
    tags: ['ale', 'german', 'wheat', 'dark', 'malty', 'weissbier'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-10c',
    guideline: 'bjcp-2021',
    categoryNumber: 10,
    categoryLetter: 'C',
    name: 'Weizenbock',
    categoryName: 'German Wheat Beer',
    vitals: {
      og: { min: 1.064, max: 1.09 },
      fg: { min: 1.015, max: 1.022 },
      ibu: { min: 15, max: 30 },
      srm: { min: 6, max: 25 },
      abv: { min: 6.5, max: 9.0 },
    },
    overallImpression:
      'A weissbier scaled up to bock strength, with intensified yeast character, dark fruit and real alcohol warmth. Pale and dark versions both exist, and either way the malt richness has to carry the strength. It should read complex and warming without ever tasting hot.',
    commercialExamples: [
      'Schneider Aventinus',
      'Weihenstephaner Vitus',
      'Plank Bavarian Dunkler Weizenbock',
      'Victory Moonglow Weizenbock',
    ],
    tags: ['ale', 'german', 'wheat', 'strong', 'malty', 'bock'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 11. British Bitter
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-11a',
    guideline: 'bjcp-2021',
    categoryNumber: 11,
    categoryLetter: 'A',
    name: 'Ordinary Bitter',
    categoryName: 'British Bitter',
    vitals: {
      og: { min: 1.03, max: 1.039 },
      fg: { min: 1.007, max: 1.011 },
      ibu: { min: 25, max: 35 },
      srm: { min: 8, max: 14 },
      abv: { min: 3.2, max: 3.8 },
    },
    overallImpression:
      'The lowest-gravity English bitter, brewed for pints rather than sips: gentle malt, restrained yeast fruit, and a bitterness that outlasts the body. Cask service means low carbonation and a soft mouthfeel. Getting real flavor at this alcohol is the whole trick.',
    commercialExamples: [
      'Adnams Southwold Bitter',
      'Brains Bitter',
      'Greene King IPA',
      'Yards Brawler',
    ],
    tags: ['ale', 'british', 'amber', 'bitter', 'sessionable', 'cask'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-11b',
    guideline: 'bjcp-2021',
    categoryNumber: 11,
    categoryLetter: 'B',
    name: 'Best Bitter',
    categoryName: 'British Bitter',
    vitals: {
      og: { min: 1.04, max: 1.048 },
      fg: { min: 1.008, max: 1.012 },
      ibu: { min: 25, max: 40 },
      srm: { min: 8, max: 16 },
      abv: { min: 3.8, max: 4.6 },
    },
    overallImpression:
      'A step up in gravity from ordinary bitter, with more malt depth and hop flavor but the same drinkable, bitter-finishing balance. English hops bring earthy and floral notes while the yeast adds light fruit. It still has to finish dry and ask for another pint.',
    commercialExamples: [
      'Timothy Taylor Landlord',
      'Coniston Bluebird Bitter',
      'Shepherd Neame Master Brew',
      'Black Sheep Best Bitter',
    ],
    tags: ['ale', 'british', 'amber', 'bitter', 'sessionable', 'cask'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-11c',
    guideline: 'bjcp-2021',
    categoryNumber: 11,
    categoryLetter: 'C',
    name: 'Strong Bitter',
    categoryName: 'British Bitter',
    vitals: {
      og: { min: 1.048, max: 1.06 },
      fg: { min: 1.01, max: 1.016 },
      ibu: { min: 30, max: 50 },
      srm: { min: 8, max: 18 },
      abv: { min: 4.6, max: 6.2 },
    },
    overallImpression:
      'The strongest bitter, often sold as ESB: richer malt, fuller body, more alcohol and correspondingly more hop flavor and bite. It stays a bitter rather than becoming an English barleywine or an IPA. Reward the examples that gain complexity without losing drinkability.',
    commercialExamples: [
      "Fuller's ESB",
      'Samuel Smith Old Brewery Pale Ale',
      'Shepherd Neame Bishops Finger',
      'Marston Pedigree',
    ],
    tags: ['ale', 'british', 'amber', 'bitter', 'malty', 'esb'],
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // 12. Pale Commonwealth Beer
  // -------------------------------------------------------------------------
  {
    id: 'bjcp-2021-12a',
    guideline: 'bjcp-2021',
    categoryNumber: 12,
    categoryLetter: 'A',
    name: 'British Golden Ale',
    categoryName: 'Pale Commonwealth Beer',
    vitals: {
      og: { min: 1.038, max: 1.053 },
      fg: { min: 1.006, max: 1.012 },
      ibu: { min: 20, max: 45 },
      srm: { min: 2, max: 6 },
      abv: { min: 3.8, max: 5.0 },
    },
    overallImpression:
      "Britain's answer to lager: pale, dry and hop-forward, brewed for summer and served cold. Malt is a clean backdrop while English and newer hop varieties supply citrus and floral notes over a crisp bitter finish. Expect less malt and more hop aroma than a bitter of the same strength.",
    commercialExamples: [
      'Fyne Ales Jarl',
      'Oakham JHB',
      'Hop Back Summer Lightning',
      'Crouch Vale Brewers Gold',
      'Kelham Island Pale Rider',
    ],
    tags: ['ale', 'british', 'pale', 'hoppy', 'sessionable', 'golden'],
    provenance: 'spec-sheet',
  },
  {
    id: 'bjcp-2021-12b',
    guideline: 'bjcp-2021',
    categoryNumber: 12,
    categoryLetter: 'B',
    name: 'Australian Sparkling Ale',
    categoryName: 'Pale Commonwealth Beer',
    vitals: {
      og: { min: 1.038, max: 1.05 },
      fg: { min: 1.004, max: 1.006 },
      ibu: { min: 20, max: 35 },
      srm: { min: 4, max: 7 },
      abv: { min: 4.5, max: 6.0 },
    },
    overallImpression:
      'An Australian pale ale traditionally bottle-conditioned, poured lively and slightly hazy once the yeast is roused. Very high attenuation leaves a strikingly dry finish carrying fruity yeast character and moderate hopping. Dryness and effervescence are what define it.',
    commercialExamples: ['Coopers Original Pale Ale', 'Coopers Sparkling Ale'],
    tags: ['ale', 'pale', 'australian', 'bottle-conditioned', 'crisp'],
    provenance: 'representative',
  },
  {
    id: 'bjcp-2021-12c',
    guideline: 'bjcp-2021',
    categoryNumber: 12,
    categoryLetter: 'C',
    name: 'English IPA',
    categoryName: 'Pale Commonwealth Beer',
    vitals: {
      og: { min: 1.05, max: 1.075 },
      fg: { min: 1.01, max: 1.018 },
      ibu: { min: 40, max: 60 },
      srm: { min: 6, max: 14 },
      abv: { min: 5.0, max: 7.5 },
    },
    overallImpression:
      'The historical English IPA: stronger and more bitter than a bitter, with English hop flavor and aroma over a firm but unsweet malt base. Yeast fruitiness and a dry finish belong; American citrus and resin do not. The balance leans bitter without tipping into a modern American IPA.',
    commercialExamples: [
      "Fuller's Bengal Lancer",
      'Meantime India Pale Ale',
      'Samuel Smith India Ale',
      'Freeminer Trafalgar IPA',
    ],
    tags: ['ale', 'british', 'pale', 'hoppy', 'bitter', 'ipa'],
    provenance: 'spec-sheet',
  },
];
